import { Option, Values } from '@lib/interfaces/form';
import _get from 'lodash/get';
import AssetFieldNames from '@lib/enums/fieldNames/assetFieldNames';
import {
  AssetCreateInputObject,
  AssetUpdateInputObject,
  DocumentationLinkInputObject,
} from 'graphql-common';
import QrCodePrefix from '@lib/enums/qrCodePrefix';
import { prepareAttachedField } from '@lib/utils/prepareAttachedField';
import mergeAndUnlinkMultiSelectValues from '@lib/utils/mergeAndUnlinkMultiSelectValues';

export default function getPreparedAssetDataFromValues({
  isCreate,
  isPlanLocked,
  prevDocumentationLinks,
  prevValues,
  values,
}: {
  isCreate?: boolean;
  isPlanLocked?: boolean;
  prevDocumentationLinks?: Option[];
  prevValues?: Values | null | undefined;
  values: Values;
}) {
  const iconAttached = prepareAttachedField(
    _get(values, AssetFieldNames.IconAttached),
    { pickFileFields: true, includeFilename: false, allowFileTag: true },
  );
  // qrPrefix is existed asset prefix (edit mode) or prefix from selected category (add mode)
  const qrPrefix =
    _get(values, `${AssetFieldNames.QrCodePrefix}.value`) ||
    _get(values, `${AssetFieldNames.Category}.data.prefix`);
  const prevQrPrefix =
    _get(prevValues, 'qrPrefix') ||
    _get(values, 'category.prefix') ||
    QrCodePrefix.Asset;
  const documentationLinksValues = (_get(
    values,
    AssetFieldNames.DocumentationLinks,
  ) || []) as Array<Option & { visibleViaQr?: boolean }>;
  const newDocLinks = documentationLinksValues.map(({ value, visibleViaQr }) =>
    visibleViaQr === undefined
      ? { id: `${value}` }
      : { id: `${value}`, visibleViaQr },
  );
  const prevDocLinks = (
    (prevDocumentationLinks || []) as Array<Option & { visibleViaQr?: boolean }>
  ).map(({ value, visibleViaQr }) =>
    visibleViaQr === undefined
      ? { id: `${value}` }
      : { id: `${value}`, visibleViaQr },
  );

  // Free-plan policy (BE assetCreate / assetUpdate):
  //   • Sending `documentationLinks` requires exactly one entry
  //     (`exactly_one_on_free_plan`).
  //   • `unlink: true` is forbidden (`unlink_forbidden_on_free_plan`).
  //   • `visibleViaQr: true` is still gated → strip it.
  // On update, omitting `documentationLinks` leaves existing links untouched
  // (so legacy / downgraded assets with >1 link stay editable on other fields).
  // The picker enforces the single-entry rule via `selectionLimit={1}` on the
  // modal, so we just forward the current selection as-is (no diff, no
  // truncation) — if the form state ever holds more than one entry the BE
  // will 422 rather than the FE silently dropping rows. Strip `visibleViaQr`
  // by projecting to `{ id }` only.
  let documentationLinks: DocumentationLinkInputObject[] | undefined;
  if (isPlanLocked) {
    if (newDocLinks.length > 0) {
      documentationLinks = newDocLinks.map(({ id }) => ({ id }));
    } else {
      documentationLinks = undefined;
    }
  } else {
    // Paid plan: diff-based merge with unlinks for retired rows.
    // `mergeAndUnlinkMultiSelectValues` returns `null` when there is no
    // add/change/unlink diff — collapse that to `undefined` so Apollo omits
    // `documentationLinks` from the payload entirely; otherwise the BE
    // receives an explicit `null` and may treat it as "clear all links".
    const mergedDocLinks =
      newDocLinks.length || prevDocLinks.length
        ? mergeAndUnlinkMultiSelectValues(prevDocLinks, newDocLinks)
        : null;
    documentationLinks = mergedDocLinks ?? undefined;
  }

  const data = {
    categoryId: _get(values, `${AssetFieldNames.Category}.value`),
    code: isCreate ? _get(values, AssetFieldNames.Code) : undefined,
    description: _get(values, AssetFieldNames.Description),
    documentationLinks,
    iconAttached,
    manufacturer: _get(values, AssetFieldNames.Manufacturer),
    model: _get(values, AssetFieldNames.Model) || '',
    name: _get(values, AssetFieldNames.Name),
    poorWifi: _get(values, AssetFieldNames.PoorWifi),
    serialNumber: _get(values, AssetFieldNames.SerialNumber) || '',
    serviceDate: _get(values, AssetFieldNames.ServiceDate),
    siteAreaId: _get(values, `${AssetFieldNames.SiteArea}.value`, null),
    siteId: isCreate
      ? _get(values, `${AssetFieldNames.Site}.value`)
      : undefined,
  };
  if (isCreate) {
    return { ...data } as AssetCreateInputObject;
  }
  return {
    ...data,
    updateQrPrefix: qrPrefix !== prevQrPrefix,
  } as unknown as AssetUpdateInputObject;
}
